import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { FC } from "react";

/**
 * A controlled date picker component without react-hook-form dependencies.
 */
export interface ControlledDateFormFieldProps {
  /** optional label text */
  label?: string;
  /** current selected date */
  value: string | null | undefined;
  /** callback invoked when date changes */
  onChange: (date: Dayjs | null) => void;
  /** disable the picker */
  isDisabled?: boolean;
  /** display error state */
  error?: boolean;
  /** helper text to display below the field */
  helperText?: string;
  minDate?: string;
  maxDate?: string;
  popperPlacement?: "bottom-start" | "bottom-end";
  enableAutoFlip?: boolean;
  /** dayjs-compatible format controlling the input field display. */
  inputFormat?: string;
}

const FALLBACK_PLACEMENTS: Record<"bottom-start" | "bottom-end", string[]> = {
  "bottom-start": ["top-start"],
  "bottom-end": ["top-end"],
};

export const ControlledDateFormField: FC<ControlledDateFormFieldProps> = ({
  label,
  value,
  onChange,
  isDisabled = false,
  error = false,
  helperText,
  minDate,
  maxDate,
  popperPlacement = "bottom-end",
  enableAutoFlip = false,
  inputFormat = "YYYY-MM-DD",
}) => {
  const dateValue = value ? dayjs(value) : null;
  const theme = useTheme();
  const modifiers = enableAutoFlip
    ? [
        {
          name: "flip",
          enabled: true,
          options: {
            fallbackPlacements: FALLBACK_PLACEMENTS[popperPlacement],
          },
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            boundary: "viewport",
            padding: 8,
          },
        },
      ]
    : undefined;
  return (
    <Box
      display="flex"
      sx={{ ".MuiFormControl-root": { width: "100%" } }}
      data-testid="DateFormField"
    >
      <FormControl sx={{ height: "100%" }} error={error}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={isDisabled}
            value={dateValue}
            onChange={onChange}
            label={label}
            inputFormat={inputFormat}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            PopperProps={{
              disablePortal: !enableAutoFlip,
              modifiers,
              placement: popperPlacement,
              sx: {
                zIndex: 1301,
                "& .MuiPaper-root": {
                  marginTop: "8px",
                },
              },
            }}
            PaperProps={{
              sx: {
                ".Mui-selected": {
                  color: "#ffffff !important",
                },
              },
            }}
            renderInput={(params: TextFieldProps) => (
              <>
                <TextField
                  {...params}
                  disabled={isDisabled}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      height: "45px",
                    },
                    "& input": {
                      fontSize: "14px",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                    },
                    fieldset: {
                      borderRadius: "6px",
                      borderColor: error
                        ? "#d32f2f !important"
                        : theme.palette.text.primary,
                    },
                  }}
                />
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
              </>
            )}
          />
        </LocalizationProvider>
      </FormControl>
    </Box>
  );
};
